import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "../api";
import type { Followup } from "../caseApi";

export const localTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"; } catch { return "America/New_York"; } };

const TABS = [["today", "Today"], ["overdue", "Overdue"], ["all", "All open"]] as const;

/** My follow-ups, by due date; "Done" removes one from the list. */
export function FollowUps() {
  const [due, setDue] = useState<(typeof TABS)[number][0]>("today");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["followups", due], queryFn: () => api<{ followups: Followup[] }>(`/api/followups?due=${due}&tz=${encodeURIComponent(localTz())}`) });
  const done = useMutation({
    mutationFn: (id: string) => api(`/api/followups/${id}`, { method: "PATCH", body: { doneAt: new Date().toISOString() } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["followups"] }),
  });
  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-4 text-xl font-semibold">Follow-ups</h1>
      <div role="tablist" className="mb-3 flex gap-1">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={due === k} onClick={() => setDue(k)}
            className={`btn ${due === k ? "bg-brand-50 text-brand-700 dark:bg-slate-800 dark:text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {q.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
      {q.error && <p role="alert" className="text-sm text-red-600">Could not load follow-ups.</p>}
      {q.data && q.data.followups.length === 0 && <p className="text-sm text-slate-500">Nothing due here.</p>}
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
        {q.data?.followups.map((f) => (
          <li key={f.id} className="flex items-center gap-3 px-4 py-3 text-sm">
            <span className="w-28 shrink-0 text-xs text-slate-500">{new Date(f.dueAt).toLocaleDateString()}</span>
            <Link to="/cases/$id" params={{ id: f.caseId }} className="font-medium text-brand-700 hover:underline dark:text-brand-100">{f.caseTitle}</Link>
            <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-400">{f.note}</span>
            <button type="button" className="btn-ghost" disabled={done.isPending} onClick={() => done.mutate(f.id)}>Done</button>
          </li>
        ))}
      </ul>
    </div>
  );
}
